export interface Actor {
  displayName: string;
  id: string;
  email?: string;
}

export enum StepType {
  userMessageStep = 'userMessageStep',
  toolInvocationStep = 'toolInvocationStep',
  ragStep = 'ragStep',
  aiMessageStep = 'aiMessageStep',
  triggerStep = 'triggerStep',
}

export enum AiService {
  m365Copilot = 'm365Copilot',
  salesforceEinstein = 'salesforceEinstein',
  copilotStudio = 'copilotStudio',
  chatGptEnterprise = 'chatGptEnterprise',
  azureAiFoundry = 'azureAiFoundry',
  vertexAi = 'vertexAi',
}

export interface Indicator {
  id: string;
}

export enum AgentClientApplication {
  office = 'office',
  teams = 'teams',
  word = 'word',
  excel = 'excel',
  powerpoint = 'powerpoint',
  outlook = 'outlook',
  onenote = 'onenote',
  appchat = 'appchat',
  sharepoint = 'sharepoint',
  stream = 'stream',
  forms = 'forms',
  bing = 'bing',
  agentWebsite = 'agentWebsite',
  testing = 'testing',
  autonomous = 'autonomous',
  chatGpt = 'chatGpt',
  azureAiFoundry = 'azureAiFoundry',
  vertexAi = 'vertexAi',
}

// apps that have icons
export enum AgentClientApplicationIcon {
  office = 'office',
  teams = 'teams',
  word = 'word',
  excel = 'excel',
  powerpoint = 'powerpoint',
  outlook = 'outlook',
  onenote = 'onenote',
  sharepoint = 'sharepoint',
  stream = 'stream',
  forms = 'forms',
  bing = 'bing',
  agentWebsite = 'agentWebsite',
  testing = 'testing',
  autonomous = 'autonomous',
  chatGpt = 'chatGpt',
  azureAiFoundry = 'azureAiFoundry',
  vertexAi = 'vertexAi',
}

export interface ClientContext {
  clientApplication: AgentClientApplication;
  region?: string;
  ipAddress?: string;
}

export enum LlmProvider {
  openAi = 'openAi',
  anthropic = 'anthropic',
  custom = 'custom',
  google = 'google',
}

export interface AIToolPlatformInfo {
  resourceId: string;
}

export interface BotPlatformInfo {
  resourceId: string;
}

export interface AIToolDefinition {
  platformInfo: AIToolPlatformInfo;
  inventoryId?: string;
  displayName?: string;
  description?: string;
  paramsDefinition?: Record<string, unknown>;
}

export enum LLMModelFamily {
  gptO1Series = 'gptO1Series',
  gpt4Series = 'gpt4Series',
  claude3Family = 'claude3Family',
  claude35Family = 'claude35Family',
  gemini = 'gemini',
}

export interface BotDetails {
  platformInfo: BotPlatformInfo;
  inventoryId?: string;
  displayName: string;
  llmProvider: LlmProvider;
  modelFamily?: LLMModelFamily;
  modelName: string;
  availableTools?: AIToolDefinition[];
}

export interface ToolInvocations {
  toolDisplayName?: string;
}

export enum SourceType {
  m365CopilotSource = 'm365CopilotSource',
  chatGptEnterpriseSource = 'chatGptEnterpriseSource',
}

export interface SourceEntry {
  id: string;
  type: SourceType;
  name?: string;
  url?: string;
}

export interface M365CopilotSource extends SourceEntry {
  sensitivityLabelId?: string;
  m365ResourceType: string;
}

export interface ChatGptEnterpriseSource extends SourceEntry {
  chatGptEnterpriseResourceType: string;
}

export interface AzureAiFoundrySource extends SourceEntry {
  azureAiFoundryResourceType: string;
}

export interface AssociatedIntegration {
  id: string;
}

export interface AssociatedThreadId {
  id: string;
}

export interface AttachedDocument {
  id: string;
  displayName: string;
}

export interface Environment {
  id: string;
  displayName: string;
}

export class BaseAgentStepModel {
  public id: string;
  public relatedActors: Actor[];
  public mainActor: Actor;
  public timestamp: Date;
  public type: StepType;
  public thread: AssociatedThreadId;
  public service: AiService;
  public indicators: Indicator[];
  public stepTriggerIds: string[];
  public botDetails: BotDetails;
  public clientContext: ClientContext;
  public zenityAccountId: string;
  public integration: AssociatedIntegration;
  public triggeringStepsIds?: string[];
  public environment?: Environment;
}

export class UserMessageAgentStepModel extends BaseAgentStepModel {
  public attachedDocuments?: AttachedDocument[];
  public isTranscriptFetchingAllowed?: boolean;
  public override type: StepType.userMessageStep;
}

export class AIMessageStep extends BaseAgentStepModel {
  public documentOutput?: AttachedDocument[];
  public isTranscriptFetchingAllowed?: boolean;
  public override type: StepType.aiMessageStep;
}

export class RagStep<TSourceEntry extends SourceEntry = SourceEntry> extends BaseAgentStepModel {
  public sources?: TSourceEntry[];
  public citedSources: TSourceEntry[];
  public ragQuery?: string;
  public override type: StepType.ragStep;
}

export class M365CopilotRagStep extends RagStep<M365CopilotSource> {
  public sensitivityLabelId?: string;
  public override service: AiService.m365Copilot;
}

export class CopilotStudioRagStep extends RagStep<M365CopilotSource> {
  public sensitivityLabelId?: string;
  public override service: AiService.copilotStudio;
}

export class M365AIMessageStep extends AIMessageStep {
  public override service: AiService.m365Copilot;
  public sensitivityLabelId?: string;
}

export class CopilotStudioAIMessageStep extends AIMessageStep {
  public override service: AiService.copilotStudio;
}

export class ChatGptEnterpriseAIMessageStep extends AIMessageStep {
  public override service: AiService.chatGptEnterprise;
}

export class AzureAiFoundryAIMessageStep extends AIMessageStep {
  public override service: AiService.azureAiFoundry;
}

export class M365CopilotUserMessageStep extends UserMessageAgentStepModel {
  public override service: AiService.m365Copilot;
  public override attachedDocuments?: AttachedDocument[];
}

export class CopilotStudioUserMessageStep extends UserMessageAgentStepModel {
  public override service: AiService.copilotStudio;
  public override attachedDocuments?: AttachedDocument[];
}

export class ChatGptEnterpriseUserMessageStep extends UserMessageAgentStepModel {
  public override service: AiService.chatGptEnterprise;
  public override attachedDocuments?: AttachedDocument[];
}

export class AzureAiFoundryUserMessageStep extends UserMessageAgentStepModel {
  public override service: AiService.azureAiFoundry;
  public override attachedDocuments?: AttachedDocument[];
}

export class VertexAiAIMessageStep extends AIMessageStep {
  public override service: AiService.vertexAi;
}

export class VertexAiUserMessageStep extends UserMessageAgentStepModel {
  public override service: AiService.vertexAi;
  public override attachedDocuments?: AttachedDocument[];
}

export class TriggerStep extends BaseAgentStepModel {
  public override type: StepType.triggerStep;
}

export class ToolInvocationStep extends BaseAgentStepModel {
  public override type: StepType.toolInvocationStep;
  public toolInvocations: ToolInvocations[];
}

export class CopilotStudioTriggerStep extends TriggerStep {
  public triggerDisplayName?: string;
  public triggerIconUrl?: string;
  public override service: AiService.copilotStudio;
}

export class CopilotStudioToolInvocationStep extends ToolInvocationStep {
  public triggerDisplayName?: string;
  public triggerIconUrl?: string;
  public override service: AiService.copilotStudio;
}

export class ChatGptEnterpriseRagStep extends RagStep<ChatGptEnterpriseSource> {
  public override service: AiService.chatGptEnterprise;
}

export class AzureAiFoundryRagStep extends RagStep<AzureAiFoundrySource> {
  public override service: AiService.azureAiFoundry;
}

export class ChatGptEnterpriseToolInvocationStep extends ToolInvocationStep {
  public override service: AiService.chatGptEnterprise;
}

export class AzureAiFoundryToolInvocationStep extends ToolInvocationStep {
  public override service: AiService.azureAiFoundry;
}

export class VertexAiToolInvocationStep extends ToolInvocationStep {
  public override service: AiService.vertexAi;
}

export type AgentStepModel =
  | CopilotStudioRagStep
  | M365CopilotRagStep
  | ChatGptEnterpriseRagStep
  | M365AIMessageStep
  | M365CopilotUserMessageStep
  | CopilotStudioTriggerStep
  | CopilotStudioToolInvocationStep
  | ChatGptEnterpriseToolInvocationStep
  | AzureAiFoundryRagStep
  | AzureAiFoundryToolInvocationStep
  | AzureAiFoundryAIMessageStep
  | AzureAiFoundryUserMessageStep
  | VertexAiToolInvocationStep
  | VertexAiAIMessageStep
  | VertexAiUserMessageStep
  | CopilotStudioAIMessageStep
  | CopilotStudioUserMessageStep
  | ChatGptEnterpriseAIMessageStep
  | ChatGptEnterpriseUserMessageStep;
